import { v4 as uuid } from "uuid";
import { assertEvent, assign, emit, fromPromise, setup } from "xstate";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";

import { summaryNoteService } from "@/services";
import { classifyApiError } from "@/services/Api";

import type { SummaryNote } from "@/models/SummaryNote";

const MAX_TIMES_TO_FETCH = 5;

function getInitialContext(input: SummaryNotesMachineInput): SummaryNotesMachineContext {
  return {
    params: input.params,
    pollCitations: input.pollCitations,
    timesFetchedSinceSaved: 0,
    error: undefined,
  };
}

export const SummaryNotesItemLogic = setup({
  types: {
    context: {} as SummaryNotesMachineContext,
    events: {} as SummaryNotesMachineEvent,
    input: {} as SummaryNotesMachineInput,
    emitted: {} as SummaryNotesMachineEmit,
  },
  actors: {
    commitSummaryText: fromPromise<
      { type: "ERROR"; data: { type: "warn" | "error"; content: string } } | { type: "SUCCESS"; reference: string },
      Pick<
        SummaryNotesMachineContext["params"],
        "evidenceTheme" | "fileReference" | "position" | "reference" | "summaryText"
      >
    >(async ({ input }) => {
      let reference = input.reference;
      const { fileReference, evidenceTheme, summaryText, position } = input;
      if (summaryText == null || summaryText.length < 1) throw new Error("Invalid Input");

      const selectedCaseName = useCaseStore().selectedCaseName;
      const { selectedClient, selectedBrand } = useBrandStore();

      if (selectedClient == null || selectedBrand == null || selectedCaseName == null)
        throw new Error("Missing Case Params");

      const action = reference == null ? "SAVE" : "UPDATE";
      reference ??= uuid();

      try {
        if (action === "SAVE") {
          await summaryNoteService.save(selectedClient, selectedBrand, selectedCaseName, fileReference, {
            reference,
            position,
            summaryText,
            confidence: 0,
            evidenceTheme,
          });
        } else {
          await summaryNoteService.update(selectedClient, selectedBrand, selectedCaseName, fileReference, reference, {
            position,
            summaryText,
          });
        }

        return { type: "SUCCESS", reference };
      } catch (error) {
        const result = classifyApiError("Summary Notes", error);

        if (result.type === "recovered") return { type: "SUCCESS", reference };

        // Return the original reference, in this case. If it was a SAVE action, this would allow the next commit attempt to be a SAVE
        // as this error is unknown
        return {
          type: "ERROR",
          data:
            result.severity === "unknown"
              ? { type: "error", content: "Unknown Error!" }
              : { type: result.severity, content: result.message },
        };
      }
    }),
    getSummaryNote: fromPromise<SummaryNote, Pick<SummaryNote, "fileReference" | "reference">>(async ({ input }) => {
      const { fileReference, reference } = input;
      if (reference == null) throw new Error("Invalid Input");

      const selectedCaseName = useCaseStore().selectedCaseName;
      const { selectedClient, selectedBrand } = useBrandStore();

      if (selectedClient == null || selectedBrand == null || selectedCaseName == null)
        throw new Error("Missing Case Params");

      return await summaryNoteService.get(selectedClient, selectedBrand, selectedCaseName, fileReference, reference);
    }),
  },
}).createMachine({
  id: "SummaryNotesItemMachine",
  initial: "idle",
  context: ({ input }) => getInitialContext(input),
  states: {
    idle: {
      on: {
        START_EDITING: { target: "editing" },
        REFRESH_SUMMARY_NOTE: { target: "polling" },
      },
    },
    editing: {
      on: {
        COMMIT: {
          target: "committing",
          guard: ({ event }) => event.value != null && event.value.length > 0,
        },
      },
    },
    committing: {
      invoke: {
        src: "commitSummaryText",
        input: ({ context, event }) => {
          assertEvent(event, "COMMIT");
          return { ...context.params, summaryText: event.value?.trim() ?? "" };
        },
        onDone: [
          {
            target: "editing",
            guard: ({ event }) => event.output?.type === "ERROR",
            actions: [
              emit({ type: "COMMIT_FAILED" } satisfies SummaryNotesMachineEmit),
              assign(({ event }) => {
                if (event.output.type !== "ERROR") throw new Error("Wrong branch");
                return { error: event.output.data };
              }),
            ],
          },
          {
            target: "polling",
            actions: assign(({ context, event }) => {
              if (event.output.type !== "SUCCESS") throw new Error("Wrong branch");

              return {
                params: { ...context.params, reference: event.output.reference },
                error: undefined,
              };
            }),
          },
        ],
      },
    },
    polling: {
      initial: "fetchingData",
      entry: assign({ timesFetchedSinceSaved: 0 }),
      states: {
        fetchingData: {
          invoke: {
            src: "getSummaryNote",
            input: ({ context }) => {
              if (context.params.reference == null) throw new Error("Missing summary note parameters");
              return { fileReference: context.params.fileReference, reference: context.params.reference };
            },
            onDone: {
              target: "checkState",
              actions: emit(({ event }) => ({ type: "COMMIT_SUCCESS", summaryNote: event.output })),
            },
            onError: {
              target: "restart",
              actions: ({ event }) => console.error("Error fetching summary note:", event),
            },
          },
          exit: assign(({ context }) => ({ timesFetchedSinceSaved: context.timesFetchedSinceSaved + 1 })),
        },
        checkState: {
          always: [
            {
              target: "#SummaryNotesItemMachine.idle",
              guard: ({ context }) =>
                // Check if max times have been reached
                context.timesFetchedSinceSaved === MAX_TIMES_TO_FETCH ||
                // Check if we should poll for citations
                !context.pollCitations ||
                // Check if there's no more citations to poll for
                !context.params.awaitingCitations,
            },
            { target: "restart" },
          ],
        },
        restart: {
          after: { 1000: { target: "fetchingData" } },
        },
      },
    },
  },
  on: {
    RESET: {
      target: ".idle",
      actions: assign(({ event }) => getInitialContext(event.input)),
    },
  },
});

type SummaryNotesMachineContext = {
  pollCitations: boolean;
  timesFetchedSinceSaved: number;
  error: { type: "warn" | "error"; content: string } | undefined;
  params: Pick<SummaryNote, "evidenceTheme" | "fileReference" | "position" | "summaryText" | "awaitingCitations"> & {
    reference: string | undefined;
  };
};

type SummaryNotesMachineEmit = { type: "COMMIT_SUCCESS"; summaryNote: SummaryNote } | { type: "COMMIT_FAILED" };

type SummaryNotesMachineInput = Pick<SummaryNotesMachineContext, "params" | "pollCitations">;

type SummaryNotesMachineEvent =
  | { type: "COMMIT"; value: string | null }
  | { type: "START_EDITING" }
  | { type: "REFRESH_SUMMARY_NOTE" }
  | { type: "RESET"; input: SummaryNotesMachineInput };
